import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, MapPin, Phone, Mail, Clock, User, Ticket as TicketIcon } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency, formatDateTime, formatDate } from "../../lib/utils-format";

export default function EventPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const locale = i18n.language === "fr" ? "fr-FR" : i18n.language === "ht" ? "fr-FR" : "en-US";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/api/events/${id}`);
                setEvent(data);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return <div className="max-w-5xl mx-auto p-10 text-muted-foreground">{t("common.loading")}</div>;
    if (!event) return <div className="max-w-5xl mx-auto p-10 text-muted-foreground">{t("common.not_found")}</div>;

    const currency = event.country?.currency_code || "USD";
    const venue = event.venue || {};
    const venueAddr = [
        venue.address,
        venue.zip_code,
        venue.city?.name,
        venue.state?.name,
        venue.country?.name,
    ]
        .filter(Boolean)
        .join(", ");

    const now = new Date();
    const isPurchasable = (tt) => {
        if (typeof tt.available === "number" && tt.available <= 0) return false;
        if (tt.sale_start_datetime && new Date(tt.sale_start_datetime) > now) return false;
        if (tt.sale_end_datetime && new Date(tt.sale_end_datetime) < now) return false;
        return true;
    };
    const hasPurchasableTickets = (event.ticket_types || []).some(isPurchasable);

    const getTicketStatus = (tt) => {
        if (typeof tt.available === "number" && tt.available <= 0) return { text: t("event.sold_out"), color: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" };
        if (tt.sale_start_datetime && new Date(tt.sale_start_datetime) > now)
            return { text: t("event.sales_not_started"), color: "bg-muted text-muted-foreground" };
        if (tt.sale_end_datetime && new Date(tt.sale_end_datetime) < now)
            return { text: t("event.sales_ended"), color: "bg-muted text-muted-foreground" };
        return { text: t("event.on_sale"), color: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" };
    };

    return (
        <div data-testid="event-page" className="animate-fade-in-up">
            {/* Hero image */}
            <div className="relative bg-foreground">
                <div className="aspect-[16/9] md:aspect-[21/9] max-h-[540px] w-full overflow-hidden">
                    {event.featured_image_url ? (
                        <img
                            src={event.featured_image_url}
                            alt={event.title}
                            className="w-full h-full object-cover"
                            data-testid="event-featured-image"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-foreground to-muted-foreground" />
                    )}
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    <div className="space-y-3 flex-1">
                        {event.categories?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {event.categories.map((c) => (
                                    <span key={c.id} className="badge-pill bg-muted text-foreground">
                                        {c.name}
                                    </span>
                                ))}
                            </div>
                        )}
                        <h1
                            data-testid="event-title"
                            className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
                        >
                            {event.title}
                        </h1>
                        {event.organizer?.name && (
                            <p className="text-muted-foreground font-medium">
                                {t("event.by")}{" "}
                                <span className="text-foreground font-semibold">{event.organizer.name}</span>
                            </p>
                        )}
                    </div>
                    <button
                        data-testid="book-btn"
                        onClick={() => hasPurchasableTickets && navigate(`/events/${event.id}/book`)}
                        disabled={!hasPurchasableTickets}
                        className="btn-primary !py-5 !px-10 text-lg w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!hasPurchasableTickets ? t("event.no_tickets_available") : undefined}
                    >
                        {hasPurchasableTickets ? t("event.book") : t("event.unavailable")}
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <InfoCard icon={<Calendar />} label={t("event.date_time")}>
                        <div className="font-semibold">{formatDateTime(event.start_datetime, locale)}</div>
                        {event.end_datetime && (
                            <div className="text-sm text-muted-foreground">
                                {t("event.to")} {formatDateTime(event.end_datetime, locale)}
                            </div>
                        )}
                    </InfoCard>
                    <InfoCard icon={<MapPin />} label={t("event.venue")}>
                        <div className="font-semibold">{venue.name || "-"}</div>
                        <div className="text-sm text-muted-foreground">{venueAddr}</div>
                    </InfoCard>
                    <InfoCard icon={<Phone />} label={t("event.contact_info")}>
                        {event.contact_phone && (
                            <div className="text-sm">
                                {event.contact_phone_code} {event.contact_phone}
                            </div>
                        )}
                        {event.contact_email && (
                            <div className="text-sm text-muted-foreground">{event.contact_email}</div>
                        )}
                    </InfoCard>
                </div>

                {/* Tickets */}
                {event.ticket_types?.length > 0 && (
                    <section>
                        <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2">
                            <TicketIcon className="h-6 w-6" />
                            {t("event.tickets")}
                        </h2>
                        <div className="space-y-3">
                            {event.ticket_types.map((tt) => {
                                const st = getTicketStatus(tt);
                                return (
                                    <div
                                        key={tt.id}
                                        data-testid={`ticket-type-${tt.id}`}
                                        className="bento-card p-5 flex items-start justify-between gap-4"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <h3 className="font-heading font-bold text-base sm:text-lg">{tt.name}</h3>
                                                <span className={`badge-pill ${st.color}`}>{st.text}</span>
                                            </div>
                                            {tt.description && (
                                                <p className="text-sm text-muted-foreground">{tt.description}</p>
                                            )}
                                            {tt.sale_start_datetime && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    <Clock className="inline h-3 w-3 mr-1" />
                                                    {formatDate(tt.sale_start_datetime, locale)}
                                                    {tt.sale_end_datetime &&
                                                        ` → ${formatDate(tt.sale_end_datetime, locale)}`}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="font-heading font-black text-lg sm:text-2xl whitespace-nowrap">
                                                {formatCurrency(tt.price, currency)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Description */}
                {event.description && (
                    <section>
                        <h2 className="font-heading font-black text-2xl mb-4">{t("event.description")}</h2>
                        <div className="bento-card p-5 md:p-6 whitespace-pre-wrap text-foreground/90 leading-relaxed">
                            {event.description}
                        </div>
                    </section>
                )}

                {/* Lineup */}
                {event.artists?.length > 0 && (
                    <section>
                        <h2 className="font-heading font-black text-2xl mb-4">{t("event.lineup")}</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {event.artists.map((a) => (
                                <div
                                    key={a.id}
                                    data-testid={`artist-${a.id}`}
                                    className="flex flex-col items-center text-center gap-2"
                                >
                                    <div className="w-24 h-24 rounded-full overflow-hidden bg-muted shadow-soft">
                                        {a.photo_url ? (
                                            <img
                                                src={a.photo_url}
                                                alt={a.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <User className="h-8 w-8 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-sm font-semibold">{a.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div className="pt-4">
                    <button
                        data-testid="book-btn-bottom"
                        onClick={() => hasPurchasableTickets && navigate(`/events/${event.id}/book`)}
                        disabled={!hasPurchasableTickets}
                        className="btn-primary w-full md:w-auto !py-5 !px-12 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!hasPurchasableTickets ? t("event.no_tickets_available") : undefined}
                    >
                        {hasPurchasableTickets ? t("event.book") : t("event.unavailable")}
                    </button>
                </div>
            </div>
        </div>
    );
}

const InfoCard = ({ icon, label, children }) => (
    <div className="bento-card p-5">
        <div className="flex items-center gap-2 mb-2 text-muted-foreground">
            <span className="inline-block [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
            <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
        </div>
        <div>{children}</div>
    </div>
);
